/**
 * V2VariableBindingSection — V2-native editor section for variable binding
 *
 * Manages the variable binding for output-capable screens:
 * - Variable selector (with "+ Create" inline)
 * - Per-screen option↔item mapping (via FunnelVariable.screenBindings)
 * - Score effects editor (via FunnelVariable.screenScoreEffects)
 *
 * Data flow (v2.1.0+):
 * - Variable is the SINGLE SOURCE OF TRUTH:
 *   - FunnelVariable.boundScreenIds — which screens this variable is bound to
 *   - FunnelVariable.screenBindings[screenId][optionId] = item.variableValue
 *   - FunnelVariable.screenScoreEffects[screenId] = ScoreEffect[]
 * - All writes go through updateVariable() → Firebase
 * - StepNode.outputBindings is NO LONGER used
 *
 * V2 paths: content.options.items.*.variableValue (read-only for binding display)
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { GuideAnchorHelp, SAccordion, SDialog, StyledTextField } from '@modules/ui-components';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import {
  variableCompatibilityService,
  variableBindingService,
  variableFactory,
  AnalyticsPrivacyDecisionField,
  type CreateVariableParams,
  type FunnelVariable,
  type VariableOption,
  type VariableType,
} from '@modules/v2-variable-utils';
import {
  V2_VARIABLES,
} from '@test-kit/selectors/variable-privacy';
import { useVariableEditor } from '../../../contexts/VariableEditorContext';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM } from '../../../config/ui';
import { EXPANDED_SECTION_BGCOLOR } from '../../../config/editorFieldStyles';
import { getValueByPath, getV2TranslationValue } from '../../../utils/v2FieldOperations';
import { getSectionTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

// ============================================================================
// CONSTANTS
// ============================================================================

const CREATE_NEW_VALUE = '__CREATE_NEW__';

/** Ensure every VariableOption has a stable `id` (backward compat for old data without id). */
const ensureOptionId = (opt: VariableOption): VariableOption & { id: string } => ({
  ...opt,
  id: opt.id || crypto.randomUUID(),
});

// ============================================================================
// TYPES
// ============================================================================

interface TableItem {
  index: number;
  variableValue: string;
  title: string;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Inline form for creating a new variable */
const InlineVariableCreator: React.FC<{
  stepKind: string;
  onCreated: (variable: FunnelVariable) => void;
  onCancel: () => void;
  disabled?: boolean;
  /** Pre-fill variable name (e.g. from screen name) */
  initialName?: string;
  /** Pre-fill options labels (e.g. from table item titles) */
  initialOptions?: string[];
  /** Existing variable names for uniqueness check */
  existingNames?: string[];
}> = ({ stepKind, onCreated, onCancel, disabled, initialName, initialOptions, existingNames = [] }) => {
  const { createVariable } = useVariableEditor();
  const compatibleTypes = variableCompatibilityService.getCompatibleTypes(stepKind);
  const defaultType = compatibleTypes[0] || 'string';

  const [name, setName] = useState(initialName || '');
  const [type, setType] = useState<VariableType>(defaultType);
  const [description, setDescription] = useState('');
  const [excludeFromAnalytics, setExcludeFromAnalytics] = useState<boolean | undefined>(undefined);
  const [options, setOptions] = useState<string[]>(
    initialOptions && initialOptions.length > 0 ? initialOptions : ['']
  );
  const [saving, setSaving] = useState(false);

  const needsOptions = type === 'enum' || type === 'enum[]';

  // Name uniqueness validation
  const nameError = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return '';
    if (existingNames.some(n => n.toLowerCase() === trimmed)) {
      return `Variable "${name.trim()}" already exists`;
    }
    return '';
  }, [name, existingNames]);

  const handleOptionChange = useCallback((index: number, value: string) => {
    setOptions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  }, []);

  const handleAddOption = useCallback(() => {
    setOptions(prev => [...prev, '']);
  }, []);

  const handleRemoveOption = useCallback((index: number) => {
    setOptions(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!name.trim() || nameError || typeof excludeFromAnalytics !== 'boolean') return;

    setSaving(true);
    try {
      const params: CreateVariableParams = {
        name: name.trim(),
        type,
        description: description.trim() || undefined,
        excludeFromAnalytics,
      };

      if (needsOptions) {
        params.options = options
          .map(label => label.trim())
          .filter(Boolean)
          .map(label => ({ id: crypto.randomUUID(), label }));
      }

      const variable = await createVariable(params);
      onCreated(variable);
    } catch (error) {
      console.error('[V2VariableBindingSection] Failed to create variable:', error);
    } finally {
      setSaving(false);
    }
  }, [
    name,
    nameError,
    type,
    description,
    excludeFromAnalytics,
    options,
    needsOptions,
    createVariable,
    onCreated,
  ]);

  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: FIELD_MARGIN_BOTTOM }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Create Variable</Typography>

      <StyledTextField
        label="Name"
        placeholder="e.g. User Goal"
        value={name}
        onChange={setName}
        disabled={disabled || saving}
        size="small"
        fullWidth
        data-testid="variable-name-input"
      />
      {nameError && (
        <Typography variant="caption" color="error.main" sx={{ mt: 0.25, display: 'block' }}>
          {nameError}
        </Typography>
      )}

      <Box sx={{ mt: 1, mb: 1 }}>
        <FormControl size="small" fullWidth>
          <Select
            value={type}
            onChange={(e: SelectChangeEvent<string>) => setType(e.target.value as VariableType)}
            disabled={disabled || saving}
            data-testid="variable-type-select"
          >
            {compatibleTypes.map(t => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <StyledTextField
        label="Description"
        placeholder="Optional description"
        value={description}
        onChange={setDescription}
        disabled={disabled || saving}
        size="small"
        fullWidth
        data-testid="variable-description-input"
      />

      <Box sx={{ mt: 1 }}>
        <AnalyticsPrivacyDecisionField
          value={excludeFromAnalytics}
          onChange={setExcludeFromAnalytics}
          testIdPrefix={V2_VARIABLES.createDecisionPrefix}
          disabled={disabled || saving}
        />
      </Box>

      {needsOptions && (
        <Box sx={{ mt: 1 }} data-testid="variable-options-list">
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Options:
          </Typography>
          {options.map((opt, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'center' }}>
              <StyledTextField
                label=""
                placeholder={`Option ${i + 1}`}
                value={opt}
                onChange={(value) => handleOptionChange(i, value)}
                disabled={disabled || saving}
                size="small"
                fullWidth
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveOption(i)}
                disabled={disabled || saving || options.length <= 1}
                sx={{ p: 0.25 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddOption}
            disabled={disabled || saving}
            sx={{ mt: 0.5 }}
          >
            Add Option
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleCreate}
          disabled={disabled
            || saving
            || !name.trim()
            || !!nameError
            || typeof excludeFromAnalytics !== 'boolean'}
          data-testid="variable-create-confirm-btn"
        >
          {saving ? 'Creating...' : 'Create'}
        </Button>
      </Box>
    </Box>
  );
};

/** Option-centric row: left = variable option label (editable), right = dropdown to bind a table item */
const ValueMappingRow: React.FC<{
  option: VariableOption & { id: string };
  /** Row position; the first row (0) carries the stable testids used by the help-article recipe. */
  index?: number;
  tableItems: TableItem[];
  boundItemVarValue: string | null;
  onRename: (optionId: string, newLabel: string) => Promise<string | null>;
  onDelete: (optionId: string) => void;
  onBindItem: (optionId: string, itemVariableValue: string | null) => void;
  disabled?: boolean;
}> = ({ option, index, tableItems, boundItemVarValue, onRename, onDelete, onBindItem, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [editLabel, setEditLabel] = useState('');
  const [error, setError] = useState('');

  const handleStartEdit = useCallback(() => {
    setEditLabel(option.label);
    setError('');
    setEditing(true);
  }, [option.label]);

  const handleConfirmEdit = useCallback(async () => {
    const trimmed = editLabel.trim();
    if (trimmed && trimmed !== option.label) {
      const err = await onRename(option.id, trimmed);
      if (err) {
        setError(err);
        return;
      }
    }
    setError('');
    setEditing(false);
  }, [editLabel, option.label, option.id, onRename]);

  const handleEditChange = useCallback((value: string) => {
    setEditLabel(value);
    if (error) setError('');
  }, [error]);

  const handleItemChange = useCallback((e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    onBindItem(option.id, val === '' ? null : val);
  }, [option.id, onBindItem]);

  return (
    <Box sx={{ mb: 0.5 }} data-testid={`variable-option-row-${option.id}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* LEFT: option label + rename/delete */}
        {editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
            <StyledTextField
              label=""
              value={editLabel}
              onChange={handleEditChange}
              disabled={disabled}
              size="small"
              fullWidth
              error={!!error}
              data-testid={`variable-option-label-input-${option.id}`}
            />
            <IconButton size="small" onClick={handleConfirmEdit} sx={{ p: 0.25 }} data-testid={`variable-option-confirm-${option.id}`}>
              <CheckIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }} data-testid={index === 0 ? 'variable-option-label' : undefined}>
              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {option.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', fontSize: '0.55rem', opacity: 0.5, lineHeight: 1, display: 'block' }}
              >
                {option.id}
              </Typography>
            </Box>
            <Tooltip title="Rename label">
              <IconButton size="small" onClick={handleStartEdit} disabled={disabled} sx={{ p: 0.25, ml: 0.25 }} data-testid={`variable-option-rename-${option.id}`}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete option">
              <IconButton size="small" onClick={() => onDelete(option.id)} disabled={disabled} sx={{ p: 0.25 }} data-testid={`variable-option-delete-${option.id}`}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>&rarr;</Typography>

        {/* RIGHT: dropdown to select which table item binds to this option */}
        <FormControl size="small" sx={{ minWidth: 140 }} data-testid={index === 0 ? 'variable-option-item-binding' : undefined}>
          <Select
            value={boundItemVarValue ?? ''}
            onChange={handleItemChange}
            disabled={disabled}
            displayEmpty
          >
            <MenuItem value="">
              <em>No binding</em>
            </MenuItem>
            {tableItems.map(ti => (
              <MenuItem key={ti.variableValue} value={ti.variableValue}>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {ti.variableValue}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', lineHeight: 1 }}>
                    {ti.title}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ pl: 0.5, mt: 0.25, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

/** Score effect editor row */
const ScoreEffectRow: React.FC<{
  index: number;
  effect: { targetVariableId: string; completionScore?: number };
  numberVariables: FunnelVariable[];
  onChange: (index: number, field: string, value: unknown) => void;
  onDelete: (index: number) => void;
  disabled?: boolean;
}> = ({ index, effect, numberVariables, onChange, onDelete, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
    <FormControl size="small" sx={{ flex: 1 }} data-testid={index === 0 ? 'variable-score-target-select' : undefined}>
      <Select
        value={effect.targetVariableId || ''}
        onChange={(e: SelectChangeEvent<string>) => onChange(index, 'targetVariableId', e.target.value)}
        disabled={disabled}
        displayEmpty
      >
        <MenuItem value="">
          <em>Select target</em>
        </MenuItem>
        {numberVariables.map(v => (
          <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <TextField
      size="small"
      type="number"
      label="Score"
      value={effect.completionScore ?? ''}
      onChange={(e) => onChange(index, 'completionScore', e.target.value ? Number(e.target.value) : undefined)}
      disabled={disabled}
      sx={{ width: 80 }}
      data-testid={index === 0 ? 'variable-score-input' : undefined}
    />
    <Tooltip title="Remove score effect">
      <IconButton size="small" onClick={() => onDelete(index)} disabled={disabled}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export interface V2VariableBindingSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  /** Rendering mode: 'standalone' wraps in SAccordion, 'embedded' renders as plain MUI Accordion */
  mode?: 'standalone' | 'embedded';
  /** Callback to report badge count to parent (used in 'embedded' mode) */
  onBadgeCount?: (count: number) => void;
  /** Section ID within a FlexibleLayout/WebEmbed. Saved as boundSectionId for runtime filtering. */
  sectionId?: string;
}

export const V2VariableBindingSection: React.FC<V2VariableBindingSectionProps> = ({
  step,
  locale,
  onChange: _onChange,
  disabled = false,
  defaultExpanded = false,
  mode = 'standalone',
  onBadgeCount,
  sectionId,
}) => {
  const theme = useTheme();
  const { variables, loading, variableUsageMap, getCompatibleVariables, updateVariable, deleteVariable } = useVariableEditor();

  // Local UI state
  const [showCreator, setShowCreator] = useState(false);
  const [scoreExpanded, setScoreExpanded] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');

  // Confirmation dialog state — delete option
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    optionId: string;
    optionLabel: string;
    affectedScreens: number;
  }>({ open: false, optionId: '', optionLabel: '', affectedScreens: 0 });

  const [applyDialog, setApplyDialog] = useState<{
    open: boolean;
    currentValues: string;
    newValues: string;
    newOptions: VariableOption[];
  }>({ open: false, currentValues: '', newValues: '', newOptions: [] });

  // Confirmation dialog state — delete variable
  const [deleteVarDialog, setDeleteVarDialog] = useState<{
    open: boolean;
    variableId: string;
    variableName: string;
    usedByScreens: number;
  }>({ open: false, variableId: '', variableName: '', usedByScreens: 0 });

  const stepKind = step.screenType || '';
  const currentScreenId = step.entityId || (step as unknown as Record<string, unknown>).id as string || '';

  // ============================================================================
  // DERIVED STATE — from FunnelVariable (single source of truth)
  // ============================================================================

  // Find the variable bound to this screen (scan all variables' boundScreenIds)
  // When sectionId is provided, match variables scoped to this section OR legacy variables
  // without boundSectionId (so they can be unbound / replaced).
  const boundVariable = useMemo<FunnelVariable | null>(() => {
    if (!currentScreenId) return null;
    const candidates = Object.values(variables).filter(v =>
      v.boundScreenIds?.includes(currentScreenId),
    );
    if (candidates.length === 0) return null;

    if (sectionId) {
      // Prefer exact section match
      const exact = candidates.find(v => {
        const vSectionId = (v as unknown as Record<string, unknown>).boundSectionId as string | undefined;
        return vSectionId === sectionId;
      });
      if (exact) return exact;
      // Fallback: legacy variable without boundSectionId (can be unbound/replaced)
      const legacy = candidates.find(v => {
        const vSectionId = (v as unknown as Record<string, unknown>).boundSectionId as string | undefined;
        return !vSectionId;
      });
      return legacy ?? null;
    }

    // Screen-level: skip section-bound variables
    return candidates.find(v => {
      const vSectionId = (v as unknown as Record<string, unknown>).boundSectionId as string | undefined;
      return !vSectionId;
    }) ?? null;
  }, [variables, currentScreenId, sectionId]);

  const boundVariableId = boundVariable?.id ?? '';

  // Compatible variables for dropdown
  const compatibleVariables = useMemo(() => {
    return getCompatibleVariables(stepKind);
  }, [getCompatibleVariables, stepKind]);

  // Whether this screen type requires value mapping (selection screens)
  const requiresMapping = variableCompatibilityService.requiresValueMapping(stepKind);

  // Number variables (for score effects target)
  const numberVariables = useMemo(() => {
    return Object.values(variables).filter(v => v.type === 'number');
  }, [variables]);

  // Items from options list (for binding display)
  const rawItems = useMemo(() => {
    const items = getValueByPath<unknown[]>(step, 'content.options.items');
    return Array.isArray(items) ? items : [];
  }, [step]);

  // Table items with variableValue + title for dropdown display
  const tableItems = useMemo<TableItem[]>(() => {
    return rawItems.map((_, idx) => {
      const varValue = getValueByPath<string>(step, `content.options.items.${idx}.variableValue`) || `item_${idx}`;
      const title = getV2TranslationValue(step, `content.options.items.${idx}.title`, locale);
      return { index: idx, variableValue: varValue, title: title || `Item ${idx + 1}` };
    });
  }, [rawItems, step, locale]);

  // Per-screen option→item mapping from variable.screenBindings
  const screenOptionMap = useMemo<Record<string, string>>(() => {
    if (!boundVariable || !currentScreenId) return {};
    return (boundVariable as unknown as Record<string, unknown>).screenBindings
      ? ((boundVariable as unknown as Record<string, Record<string, Record<string, string>>>).screenBindings?.[currentScreenId] ?? {})
      : {};
  }, [boundVariable, currentScreenId]);

  // Score effects for current screen from variable.screenScoreEffects
  const scoreEffects = useMemo(() => {
    if (!boundVariable || !currentScreenId) return [];
    return (boundVariable as unknown as Record<string, Record<string, unknown[]>>).screenScoreEffects?.[currentScreenId] ?? [];
  }, [boundVariable, currentScreenId]);

  // ============================================================================
  // BINDING DIAGNOSTICS
  // ============================================================================

  interface BindingWarning {
    id: string;
    type: 'unmapped-item' | 'orphan-option' | 'shared-item' | 'no-variable';
    severity: 'warning' | 'error' | 'info';
    message: string;
    /** For unmapped-item: the item data. For orphan-option: the option data. */
    context?: { variableValue?: string; title?: string; optionId?: string; label?: string };
  }

  const bindingWarnings = useMemo<BindingWarning[]>(() => {
    if (!requiresMapping) return [];

    // W5: No variable bound to selection screen
    if (!boundVariable) {
      return [{
        id: 'w5-no-variable',
        type: 'no-variable',
        severity: 'info',
        message: 'No variable bound. User selections won\'t be saved.',
      }];
    }

    const warnings: BindingWarning[] = [];
    const options = boundVariable.options ?? [];
    const bindingValues = new Set(Object.values(screenOptionMap));
    const boundOptionIds = new Set(Object.keys(screenOptionMap));

    // W1: Unbound items — variableValue not targeted by any screenBinding entry
    for (const item of tableItems) {
      if (!bindingValues.has(item.variableValue)) {
        warnings.push({
          id: `w1-${item.variableValue}`,
          type: 'unmapped-item',
          severity: 'warning',
          message: `Item "${item.title}" (${item.variableValue}) is not mapped to any option`,
          context: { variableValue: item.variableValue, title: item.title },
        });
      }
    }

    // W2: Orphan options — no screenBinding entry for this screen
    for (const opt of options) {
      if (!boundOptionIds.has(opt.id)) {
        warnings.push({
          id: `w2-${opt.id}`,
          type: 'orphan-option',
          severity: 'warning',
          message: `Option "${opt.label}" is not mapped to any list item`,
          context: { optionId: opt.id, label: opt.label },
        });
      }
    }

    // W3: Shared items — 2+ options mapped to the same variableValue
    const valueToOptions = new Map<string, string[]>();
    for (const [optId, varVal] of Object.entries(screenOptionMap)) {
      const arr = valueToOptions.get(varVal) || [];
      arr.push(optId);
      valueToOptions.set(varVal, arr);
    }
    for (const [varVal, optIds] of valueToOptions) {
      if (optIds.length > 1) {
        const labels = optIds.map(id => options.find(o => o.id === id)?.label ?? id);
        warnings.push({
          id: `w3-${varVal}`,
          type: 'shared-item',
          severity: 'warning',
          message: `Options "${labels.join('", "')}" both map to item "${varVal}"`,
        });
      }
    }

    return warnings;
  }, [requiresMapping, boundVariable, screenOptionMap, tableItems]);

  const bindingWarningCount = bindingWarnings.length;

  // Report badge count to parent when in embedded mode
  useEffect(() => {
    if (onBadgeCount) {
      onBadgeCount(bindingWarningCount);
    }
  }, [onBadgeCount, bindingWarningCount]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Auto-create variable options from screen items when variable has none.
   * Uses item variableValue (item_N) as label — the stable item identifier.
   */
  const createOptionsFromItems = useCallback((): VariableOption[] => {
    return rawItems.map((_, index) => {
      const varValue = getValueByPath<string>(step, `content.options.items.${index}.variableValue`) || `item_${index}`;
      return { id: crypto.randomUUID(), label: varValue };
    });
  }, [rawItems, step, locale]);

  /** Binding context for current screen/section */
  const bindingCtx = useMemo(() => ({ screenId: currentScreenId, sectionId }), [currentScreenId, sectionId]);

  /** Screen items context for auto-populating screenBindings */
  const screenItemsCtx = useMemo(
    () => (requiresMapping && tableItems.length > 0) ? { items: tableItems } : undefined,
    [requiresMapping, tableItems],
  );

  /** Handle variable selection change */
  const handleVariableChange = useCallback(async (event: SelectChangeEvent<string>) => {
    const variableId = event.target.value;

    if (variableId === CREATE_NEW_VALUE) {
      setShowCreator(true);
      return;
    }

    if (!variableId) {
      // Unbind: remove current screen from the bound variable
      if (boundVariable) {
        await updateVariable(variableBindingService.unbind(boundVariable, bindingCtx));
      }
      return;
    }

    // Switch or bind
    const newVariable = variables[variableId];
    if (!newVariable) return;

    if (boundVariable && boundVariable.id !== variableId) {
      // Switch: unbind old + bind new
      const { updatedVariables } = variableBindingService.switch(
        boundVariable, newVariable, bindingCtx, screenItemsCtx,
      );
      for (const v of updatedVariables) {
        await updateVariable(v);
      }
    } else {
      // Fresh bind
      await updateVariable(variableBindingService.bind(newVariable, bindingCtx, screenItemsCtx));
    }
  }, [boundVariable, variables, bindingCtx, screenItemsCtx, updateVariable]);

  /** Request deletion of a variable — open confirmation dialog */
  const handleDeleteVariableRequest = useCallback((varId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const v = variables[varId];
    if (!v) return;
    const usedBy = variableUsageMap[varId] ?? [];
    setDeleteVarDialog({
      open: true,
      variableId: varId,
      variableName: v.name,
      usedByScreens: usedBy.length,
    });
  }, [variables, variableUsageMap]);

  /** Confirm variable deletion */
  const handleDeleteVariableConfirm = useCallback(async () => {
    const { variableId } = deleteVarDialog;
    setDeleteVarDialog(prev => ({ ...prev, open: false }));

    try {
      await deleteVariable(variableId);
    } catch (error) {
      console.error('[V2VariableBindingSection] Failed to delete variable:', error);
    }
  }, [deleteVarDialog, deleteVariable]);

  /** Handle inline variable creation complete — unbind old variable, then auto-bind new one */
  const handleVariableCreated = useCallback(async (variable: FunnelVariable) => {
    setShowCreator(false);

    // Unbind old variable if one was already bound
    if (boundVariable) {
      await updateVariable(variableBindingService.unbind(boundVariable, bindingCtx));
    }

    // Bind new variable to current screen
    await updateVariable(variableBindingService.bind(variable, bindingCtx, screenItemsCtx));
  }, [boundVariable, bindingCtx, screenItemsCtx, updateVariable]);

  /** Handle score effect changes — write to variable.screenScoreEffects */
  const handleScoreEffectChange = useCallback(async (index: number, field: string, value: unknown) => {
    if (!boundVariable) return;
    const currentEffects = [...scoreEffects] as Record<string, unknown>[];
    currentEffects[index] = { ...(currentEffects[index] as Record<string, unknown>), [field]: value };
    await updateVariable(variableBindingService.updateScoreEffects(boundVariable, currentScreenId, currentEffects));
  }, [boundVariable, scoreEffects, currentScreenId, updateVariable]);

  const handleAddScoreEffect = useCallback(async () => {
    if (!boundVariable) return;
    const updatedEffects = [...scoreEffects, { targetVariableId: '', completionScore: 1 }];
    await updateVariable(variableBindingService.updateScoreEffects(boundVariable, currentScreenId, updatedEffects));
  }, [boundVariable, scoreEffects, currentScreenId, updateVariable]);

  const handleDeleteScoreEffect = useCallback(async (index: number) => {
    if (!boundVariable) return;
    const updatedEffects = scoreEffects.filter((_, i) => i !== index);
    if (updatedEffects.length > 0) {
      await updateVariable(variableBindingService.updateScoreEffects(boundVariable, currentScreenId, updatedEffects));
    } else {
      // Remove entire screen entry when no effects remain
      await updateVariable(variableBindingService.updateScoreEffects(boundVariable, currentScreenId, []));
    }
  }, [boundVariable, scoreEffects, currentScreenId, updateVariable]);

  /** Add a new option to the bound variable. Not mapped to any item until user binds via dropdown. */
  const handleAddVariableOption = useCallback(async () => {
    if (!boundVariable || !newOptionLabel.trim()) return;
    const label = newOptionLabel.trim();
    const newOption: VariableOption = { id: crypto.randomUUID(), label };
    const updatedVariable: FunnelVariable = {
      ...boundVariable,
      options: [...(boundVariable.options || []), newOption],
    };
    try {
      await updateVariable(updatedVariable);
      setNewOptionLabel('');
    } catch (error) {
      console.error('[V2VariableBindingSection] Failed to add variable option:', error);
    }
  }, [boundVariable, newOptionLabel, updateVariable]);

  /** Rename option LABEL. Finds by option.id. Value stays immutable. */
  const handleRenameOption = useCallback(async (optionId: string, newLabel: string): Promise<string | null> => {
    if (!boundVariable) return 'No variable bound';

    const currentLabel = (boundVariable.options || []).find(o => o.id === optionId)?.label;
    if (newLabel !== currentLabel && (boundVariable.options || []).some(o => o.label.toLowerCase() === newLabel.toLowerCase())) {
      return `Label "${newLabel}" already exists`;
    }

    const updatedOptions = (boundVariable.options || []).map(opt =>
      opt.id === optionId ? { ...opt, label: newLabel } : opt
    );
    try {
      await updateVariable({ ...boundVariable, options: updatedOptions });
      return null;
    } catch (error) {
      console.error('[V2VariableBindingSection] Failed to rename variable option label:', error);
      return 'Failed to save';
    }
  }, [boundVariable, updateVariable]);

  /** Delete a variable option — open confirmation dialog */
  const handleDeleteOption = useCallback((optionId: string) => {
    if (!boundVariable) return;
    const option = (boundVariable.options || []).find(o => o.id === optionId);
    if (!option) return;

    // Count how many screens have this option in screenBindings
    const screenBindings = (boundVariable as unknown as Record<string, Record<string, Record<string, string>>>).screenBindings || {};
    const affectedScreens = Object.values(screenBindings).filter(mapping =>
      Object.keys(mapping).includes(optionId)
    ).length;

    setDeleteDialog({ open: true, optionId, optionLabel: option.label, affectedScreens });
  }, [boundVariable]);

  /** Confirm delete — remove option from variable and clean up screenBindings */
  const handleDeleteConfirm = useCallback(async () => {
    if (!boundVariable) return;
    const { optionId } = deleteDialog;
    setDeleteDialog(prev => ({ ...prev, open: false }));

    const updatedOptions = (boundVariable.options || []).filter(opt => opt.id !== optionId);

    // Clean up screenBindings — remove this option's entries from all screens
    const screenBindings = { ...((boundVariable as unknown as Record<string, Record<string, Record<string, string>>>).screenBindings || {}) };
    for (const screenId of Object.keys(screenBindings)) {
      if (screenBindings[screenId]?.[optionId]) {
        const { [optionId]: _, ...rest } = screenBindings[screenId];
        screenBindings[screenId] = rest;
      }
    }

    try {
      await updateVariable({
        ...boundVariable,
        options: updatedOptions,
        screenBindings,
      } as FunnelVariable);
    } catch (error) {
      console.error('[V2VariableBindingSection] Failed to delete variable option:', error);
    }
  }, [boundVariable, deleteDialog, updateVariable]);

  /** Apply screen items to variable — re-create options from current items */
  const handleApplyItemsToVariable = useCallback(() => {
    if (!boundVariable) return;

    const newOptions = createOptionsFromItems();

    const currentLabels = (boundVariable.options || []).map(o => o.label).sort().join(',');
    const newLabels = newOptions.map(o => o.label).sort().join(',');
    if (currentLabels === newLabels) return;

    setApplyDialog({
      open: true,
      currentValues: (boundVariable.options || []).map(o => o.label).join(', '),
      newValues: newOptions.map(o => o.label).join(', '),
      newOptions,
    });
  }, [boundVariable, createOptionsFromItems]);

  /** Confirm apply — overwrite variable options and rebuild screenBindings for current screen */
  const handleApplyConfirm = useCallback(async () => {
    if (!boundVariable) return;
    const { newOptions } = applyDialog;
    setApplyDialog(prev => ({ ...prev, open: false }));

    const opts = newOptions.map(ensureOptionId);
    const newBindings = variableBindingService.buildPositionalBindings(opts, tableItems);

    try {
      const updated = variableBindingService.rebuildScreenBindings(
        { ...boundVariable, options: opts },
        currentScreenId,
        newBindings,
      );
      await updateVariable(updated);
    } catch (error) {
      console.error('[V2VariableBindingSection] Failed to apply items to variable:', error);
    }
  }, [boundVariable, applyDialog, currentScreenId, tableItems, updateVariable]);

  /** Bind a table item to a variable option via screenBindings */
  const handleBindItemToOption = useCallback(async (optionId: string, itemVariableValue: string | null) => {
    if (!boundVariable) return;

    const currentBindings = { ...screenOptionMap };

    if (itemVariableValue) {
      currentBindings[optionId] = itemVariableValue;
    } else {
      delete currentBindings[optionId];
    }

    await updateVariable(
      variableBindingService.rebuildScreenBindings(boundVariable, currentScreenId, currentBindings),
    );
  }, [boundVariable, screenOptionMap, currentScreenId, updateVariable]);

  // ============================================================================
  // AUTO-FIX HANDLERS (for binding warnings)
  // ============================================================================

  /** W1 fix: Create a new variable option and map it to an unbound item */
  const handleAutoCreateOption = useCallback(async (itemVariableValue: string) => {
    if (!boundVariable) return;
    const newOption: VariableOption = { id: crypto.randomUUID(), label: itemVariableValue };
    const updatedVar = { ...boundVariable, options: [...(boundVariable.options || []), newOption] };
    await updateVariable(
      variableBindingService.rebuildScreenBindings(
        updatedVar,
        currentScreenId,
        { ...screenOptionMap, [newOption.id]: itemVariableValue },
      ),
    );
  }, [boundVariable, screenOptionMap, currentScreenId, updateVariable]);

  /** W5 fix: Create a default enum variable bound to this screen with positional bindings */
  const handleAutoCreateVariable = useCallback(async () => {
    // Build options from existing list items
    const options: VariableOption[] = tableItems.map((item) => ({
      id: crypto.randomUUID(),
      label: item.variableValue,
    }));

    const variableName = step.name || `${stepKind} Selection`;
    const variableType = (variableCompatibilityService.getCompatibleTypes(stepKind)[0] || 'enum') as VariableType;

    // handleVariableCreated adds boundScreenIds, auto-populates screenBindings from items.
    // Use a slug id instead of a UUID so downstream @variable mentions stay readable.
    await handleVariableCreated(variableFactory.create({
      id: variableFactory.generateId(variableName, Object.keys(variables)),
      name: variableName,
      type: variableType,
      required: true,
      options,
    }) as FunnelVariable);
  }, [tableItems, step.name, stepKind, variables, handleVariableCreated]);

  // Multi-screen variable usage warning
  const otherScreensUsingVariable = useMemo(() => {
    if (!boundVariableId || !variableUsageMap[boundVariableId]) return [];
    return variableUsageMap[boundVariableId].filter(id => id !== currentScreenId);
  }, [boundVariableId, variableUsageMap, currentScreenId]);

  // ============================================================================
  // AUTO-POPULATE screenBindings ON LOAD
  // ============================================================================
  // When a screen has an existing variable binding but no screenBindings entry
  // for this screen (e.g., migrated from old outputBindings data), build it once.

  const autoPopulatedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!boundVariable || !requiresMapping || tableItems.length === 0 || loading) return;
    if (autoPopulatedRef.current === boundVariableId) return;
    autoPopulatedRef.current = boundVariableId;

    // Check if screenBindings for this screen already exist and have entries
    const existingBindings = (boundVariable as unknown as Record<string, Record<string, Record<string, string>>>).screenBindings?.[currentScreenId];
    if (existingBindings && Object.keys(existingBindings).length > 0) return;

    // Need to auto-populate — use facade to bind (which auto-populates screenBindings)
    const itemsCtx = tableItems.length > 0 ? { items: tableItems } : undefined;
    const updated = variableBindingService.bind(boundVariable, { screenId: currentScreenId }, itemsCtx);
    updateVariable(updated).catch(err =>
      console.error('[V2VariableBindingSection] Failed to auto-populate on load:', err)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundVariableId, boundVariable, requiresMapping, tableItems.length, loading]);

  // ============================================================================
  // RENDER
  // ============================================================================

  const SECTION_NAME = 'Variable Binding';
  const sectionTestId = getSectionTestId(SECTION_NAME);

  // ── Shared content (rendered inside both standalone and embedded wrappers) ──

  const renderLoadingContent = () => (
    <Typography variant="body2" color="text.secondary">Loading variables...</Typography>
  );

  const renderContent = () => (
    <>
      {/* Variable Selector */}
      <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>Variable</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={boundVariableId}
              onChange={handleVariableChange}
              disabled={disabled}
              displayEmpty
              data-testid="variable-selector"
              renderValue={(selected) => {
                if (!selected) return <em>None (no binding)</em>;
                const v = variables[selected as string];
                return v ? `${v.name} (${v.type})` : String(selected);
              }}
            >
              <MenuItem value="">
                <em>None (no binding)</em>
              </MenuItem>
              {compatibleVariables.map(v => (
                <MenuItem key={v.id} value={v.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                    {v.name}
                    <Chip
                      label={v.type}
                      size="small"
                      sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteVariableRequest(v.id, e)}
                    data-testid={`variable-delete-${v.name}`}
                    sx={{ p: 0.25, ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </MenuItem>
              ))}
              <MenuItem value={CREATE_NEW_VALUE} data-testid="variable-create-btn">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                  <AddIcon fontSize="small" />
                  Create Variable
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Inline Variable Creator */}
      <Collapse in={showCreator} timeout="auto">
        <InlineVariableCreator
          stepKind={stepKind}
          onCreated={handleVariableCreated}
          onCancel={() => setShowCreator(false)}
          disabled={disabled}
          initialName={step.name || ''}
          initialOptions={requiresMapping && rawItems.length > 0
            ? rawItems.map((_, idx) =>
                getValueByPath<string>(step, `content.options.items.${idx}.variableValue`) || `item_${idx}`
              )
            : undefined
          }
          existingNames={Object.values(variables).map(v => v.name)}
        />
      </Collapse>

      {/* Bound variable info */}
      {boundVariable && (
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label={`Type: ${boundVariable.type}`} size="small" variant="outlined" />
          {boundVariable.required && <Chip label="Required" size="small" color="warning" variant="outlined" />}
          {boundVariable.description && (
            <Tooltip title={boundVariable.description}>
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                {boundVariable.description.length > 40
                  ? `${boundVariable.description.slice(0, 40)}...`
                  : boundVariable.description}
              </Typography>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Multi-screen variable warning */}
      {otherScreensUsingVariable.length > 0 && (
        <Alert severity="info" variant="outlined" sx={{ mb: FIELD_MARGIN_BOTTOM, py: 0.5 }}>
          <Typography variant="caption">
            This variable is also used by {otherScreensUsingVariable.length} other screen(s).
            Changes to options will affect all screens. Item bindings are per-screen.
          </Typography>
        </Alert>
      )}

      {/* Variable Options (option-centric: left = option label, right = table item dropdown) */}
      {requiresMapping && boundVariable && (boundVariable.options ?? []).length > 0 && (
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM }} data-testid="variable-options-section">
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>Variable Options</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Manage variable option names. Select table items on the right for per-screen binding.
          </Typography>
          {(boundVariable.options ?? []).map(ensureOptionId).map((opt, optIndex) => {
            const boundItemVarValue = screenOptionMap[opt.id] || null;

            return (
              <ValueMappingRow
                key={opt.id}
                option={opt}
                index={optIndex}
                tableItems={tableItems}
                boundItemVarValue={boundItemVarValue}
                onRename={handleRenameOption}
                onDelete={handleDeleteOption}
                onBindItem={handleBindItemToOption}
                disabled={disabled}
              />
            );
          })}

          {/* Add new option */}
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, alignItems: 'center' }}>
            <StyledTextField
              label=""
              placeholder="New option label"
              value={newOptionLabel}
              onChange={setNewOptionLabel}
              disabled={disabled}
              size="small"
              fullWidth
              data-testid="variable-new-option-input"
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleAddVariableOption}
              disabled={disabled || !newOptionLabel.trim()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add
            </Button>
          </Box>

          {/* Apply Items to Variable — re-sync variable options from current list items */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleApplyItemsToVariable}
              disabled={disabled || !boundVariable}
              startIcon={<SyncIcon />}
              sx={{ textTransform: 'none' }}
              data-testid="variable-apply-items-btn"
            >
              Apply Items to Variable
            </Button>
          </Box>
        </Box>
      )}

      {/* Score Effects (expandable) */}
      {boundVariable && (
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 0.5 }}
            onClick={() => setScoreExpanded(!scoreExpanded)}
            data-testid="variable-score-effects-header"
          >
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: scoreExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s',
                mr: 0.5,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Score Effects
              {scoreEffects.length > 0 && (
                <Chip
                  label={scoreEffects.length}
                  size="small"
                  sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                />
              )}
            </Typography>
          </Box>

          <Collapse in={scoreExpanded} timeout="auto">
            <Box sx={{ pl: 2 }}>
              {scoreEffects.map((effect, index) => (
                <ScoreEffectRow
                  key={index}
                  index={index}
                  effect={effect as { targetVariableId: string; completionScore?: number }}
                  numberVariables={numberVariables}
                  onChange={handleScoreEffectChange}
                  onDelete={handleDeleteScoreEffect}
                  disabled={disabled}
                />
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddScoreEffect}
                disabled={disabled}
                sx={{ mt: 0.5 }}
              >
                Add Score Effect
              </Button>
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Binding Warnings */}
      {bindingWarnings.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {bindingWarnings.map(w => (
            <Alert
              key={w.id}
              severity={w.severity}
              variant="outlined"
              sx={{ mb: 0.5, py: 0, '& .MuiAlert-message': { py: 0.5 } }}
              action={
                w.type === 'unmapped-item' && w.context ? (
                  <Button
                    size="small"
                    onClick={() => handleAutoCreateOption(w.context!.variableValue!)}
                  >
                    Create &amp; Map
                  </Button>
                ) : w.type === 'orphan-option' && w.context ? (
                  <Button
                    size="small"
                    onClick={() => {
                      // Auto-map to first unmapped item
                      const bindingValues = new Set(Object.values(screenOptionMap));
                      const freeItem = tableItems.find(ti => !bindingValues.has(ti.variableValue));
                      if (freeItem) handleBindItemToOption(w.context!.optionId!, freeItem.variableValue);
                    }}
                    disabled={(() => {
                      const bindingValues = new Set(Object.values(screenOptionMap));
                      return !tableItems.some(ti => !bindingValues.has(ti.variableValue));
                    })()}
                  >
                    Auto-Map
                  </Button>
                ) : w.type === 'no-variable' ? (
                  <Button size="small" onClick={handleAutoCreateVariable}>
                    Create Variable
                  </Button>
                ) : undefined
              }
            >
              <Typography variant="caption">{w.message}</Typography>
            </Alert>
          ))}
        </Box>
      )}
    </>
  );

  // ── Dialogs (rendered outside any accordion to avoid portal issues) ──

  const renderDialogs = () => (
    <>
      {/* Delete Option Confirmation Dialog */}
      <SDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog(prev => ({ ...prev, open: false }))}
        title="Delete Variable Option?"
        maxWidth="xs"
        fullWidth
        showCloseButton={false}
        primaryAction={{
          label: 'Delete',
          onClick: handleDeleteConfirm,
          color: 'error',
          variant: 'contained',
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setDeleteDialog(prev => ({ ...prev, open: false })),
          variant: 'outlined',
        }}
      >
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">
            Are you sure you want to delete option &quot;{deleteDialog.optionLabel}&quot;?
          </Typography>
          {deleteDialog.affectedScreens > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This option has bindings on {deleteDialog.affectedScreens} screen(s) that will be removed.
            </Typography>
          )}
        </Box>
      </SDialog>

      {/* Apply Items to Variable Confirmation Dialog */}
      <SDialog
        open={applyDialog.open}
        onClose={() => setApplyDialog(prev => ({ ...prev, open: false }))}
        title="Overwrite Variable Options?"
        maxWidth="xs"
        fullWidth
        showCloseButton={false}
        primaryAction={{
          label: 'Overwrite',
          onClick: handleApplyConfirm,
          color: 'warning',
          variant: 'contained',
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setApplyDialog(prev => ({ ...prev, open: false })),
          variant: 'outlined',
        }}
      >
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">
            Variable options will be replaced with values from table items.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Current: {applyDialog.currentValues || '(none)'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            New: {applyDialog.newValues}
          </Typography>
        </Box>
      </SDialog>

      {/* Delete Variable Confirmation Dialog */}
      <SDialog
        open={deleteVarDialog.open}
        onClose={() => setDeleteVarDialog(prev => ({ ...prev, open: false }))}
        title="Delete Variable?"
        maxWidth="xs"
        fullWidth
        primaryAction={{
          label: 'Delete',
          onClick: handleDeleteVariableConfirm,
          color: 'error',
          variant: 'contained',
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setDeleteVarDialog(prev => ({ ...prev, open: false })),
          variant: 'outlined',
        }}
      >
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">
            Are you sure you want to delete variable &quot;{deleteVarDialog.variableName}&quot;?
          </Typography>
          {deleteVarDialog.usedByScreens > 0 && (
            <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
              This variable is used by {deleteVarDialog.usedByScreens} screen(s). Their bindings will break.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </Box>
      </SDialog>
    </>
  );

  // ── Embedded mode: plain MUI Accordion (for rendering inside parent SAccordion) ──

  /** Accordion sx matching SectionLayoutSettings style */
  const embeddedAccordionSx = {
    backgroundColor: 'transparent',
    '&:before': { display: 'none' },
    '& .MuiAccordionSummary-root': {
      minHeight: 32,
      px: 1,
      py: 0,
      backgroundColor: theme.palette.action.hover,
      borderRadius: 1,
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
    },
    '& .MuiAccordionSummary-content': {
      margin: '4px 0',
    },
    '& .MuiAccordionDetails-root': {
      px: 2,
      pt: 1,
      pb: 1,
      bgcolor: EXPANDED_SECTION_BGCOLOR,
      borderRadius: 1,
    },
  };

  if (mode === 'embedded') {
    if (loading) {
      return (
        <>
          <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            elevation={0}
            sx={embeddedAccordionSx}
            data-testid={sectionTestId}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" fontWeight={500}>{SECTION_NAME}</Typography>
                <GuideAnchorHelp ownerTestId={sectionTestId} />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderLoadingContent()}
            </AccordionDetails>
          </Accordion>
          {renderDialogs()}
        </>
      );
    }

    return (
      <>
        <Accordion
          defaultExpanded={defaultExpanded}
          disableGutters
          elevation={0}
          sx={embeddedAccordionSx}
          data-testid={sectionTestId}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" fontWeight={500}>{SECTION_NAME}</Typography>
              <GuideAnchorHelp ownerTestId={sectionTestId} />
              {bindingWarningCount > 0 && (
                <Chip
                  label={bindingWarningCount}
                  size="small"
                  color="warning"
                  sx={{ height: 20, minWidth: 20, fontSize: '0.7rem', fontWeight: 600 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {renderContent()}
          </AccordionDetails>
        </Accordion>
        {renderDialogs()}
      </>
    );
  }

  // ── Standalone mode (default): SAccordion wrapper ──

  if (loading) {
    return (
      <>
        <SAccordion
          title={SECTION_NAME}
          collapsible
          defaultExpanded={defaultExpanded}
          sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
          data-testid={sectionTestId}
        >
          {renderLoadingContent()}
        </SAccordion>
        {renderDialogs()}
      </>
    );
  }

  return (
    <>
      <SAccordion
        title={SECTION_NAME}
        badge={bindingWarningCount}
        badgeColor="warning"
        collapsible
        defaultExpanded={defaultExpanded}
        sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
        data-testid={sectionTestId}
      >
        {renderContent()}
      </SAccordion>
      {renderDialogs()}
    </>
  );
};
